import axios from "@/plugins/axios";

/**
 * Normalize an establishment↔catalog link into a catalog object for UI/API calls.
 *
 * @param {object} link
 * @returns {object|null}
 */
export function catalogFromEstablishmentLink(link) {
    if (!link) {
        return null;
    }
    const catalogData = link.catalog_data;
    const catalogId = catalogData?.id || link.catalog?.id || link.catalog_id;
    if (!catalogId) {
        return null;
    }
    if (catalogData?.id) {
        return catalogData;
    }
    if (link.catalog && typeof link.catalog === "object" && link.catalog.id) {
        return link.catalog;
    }
    return { id: catalogId };
}

/**
 * Returns sales catalogs linked to an establishment, ordered by priority.
 *
 * @param {number|string} establishmentId
 * @returns {Promise<object[]>} EstablishmentCatalog link rows
 */
export async function fetchLinkedCatalogs(establishmentId) {
    const response = await axios.get(
        `/api/establishments/${establishmentId}/establishment-catalogs/`,
        { params: { ordering: "priority", limit: 1000 } },
    );
    return response.data.results || response.data || [];
}

/**
 * Returns the highest-priority sales catalog linked to an establishment.
 *
 * @param {number|string} establishmentId
 * @returns {Promise<object|null>}
 */
export async function fetchPrimaryLinkedCatalog(establishmentId) {
    const links = await fetchLinkedCatalogs(establishmentId);
    if (!links.length) {
        return null;
    }
    return catalogFromEstablishmentLink(links[0]);
}

/**
 * Establishment-related UI (form fields, table filters and columns) is only
 * relevant for customer entities that have the "establishments" feature
 * enabled (always on for retailers). Other profiles (e.g. operators) keep
 * the permission-only check.
 *
 * @param {import('vue').Vue} vm - component instance with GlobalMixins
 *   (provides userHasPermission, current_entity_profile,
 *   currentEntityHasFeature).
 * @returns {boolean}
 */
export function canAccessEstablishments(vm) {
    if (!vm.userHasPermission("core.view_establishment")) {
        return false;
    }
    if (vm.current_entity_profile === "customer") {
        return Boolean(vm.currentEntityHasFeature("establishments"));
    }
    return true;
}
